<template>
  <v-card class="shadow-sm">
    <div class="bg-surface border-b px-4 pt-2">
      <div class="d-flex align-center justify-space-between">
        <div class="text-subtitle-1 font-weight-bold">Notifications</div>

        <v-btn
            variant="text"
            size="small"
            color="primary"
            class="text-none"
            prepend-icon="mdi-check-all"
            :disabled="!unreadCount"
            :loading="markingAll"
            @click="markAllAsRead"
        >
          Tout marquer comme lu
        </v-btn>
      </div>

      <div class="d-flex align-center justify-space-between">
        <v-tabs v-model="category" color="primary" density="compact">
          <v-tab :value="CATEGORY_ALERTS" class="text-none">Alertes</v-tab>
          <v-tab :value="CATEGORY_ALL" class="text-none">Tout</v-tab>
        </v-tabs>

        <v-switch
            label="Uniquement les non lus"
            hide-details
            color="primary"
            :true-value="true"
            :false-value="false"
            :model-value="onlyUnread"
            @update:modelValue="onlyUnread = !!$event"
            density="compact"
            class="ml-4 flex-grow-0"
        ></v-switch>
      </div>
    </div>

    <v-card-text class="pa-0">
      <div v-if="!notifications.length && !loading && !hasMore" class="d-flex flex-column align-center justify-center pa-8 text-center">
        <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-bell-off-outline</v-icon>
        <div class="text-h6 text-medium-emphasis mb-1">Aucune notification</div>
        <div class="text-body-2 text-medium-emphasis">Vous êtes à jour !</div>
      </div>

      <div v-else class="d-flex flex-column">
        <div
            v-for="(notification, index) in notifications"
            :key="notification.id"
            class="notification-item px-4 py-3 cursor-pointer transition-swing"
            :class="{
                  'bg-surface': notification.read_at,
                  'bg-primary-lighten-5': !notification.read_at,
                  'border-b': index < notifications.length - 1
                }"
            :data-type="notification.type"
            @click="open(notification)"
        >
          <div class="d-flex align-start ga-3">
            <div class="pt-1">
              <v-avatar
                  size="40"
                  :color="getNotificationColor(notification.type)"
                  variant="flat"
              >
                <v-icon color="white" size="small">
                  {{ getNotificationIcon(notification.type) }}
                </v-icon>
              </v-avatar>
            </div>

            <div class="flex-grow-1 min-w-0">
              <div class="d-flex justify-space-between align-start mb-1">
                <div class="d-flex align-center min-w-0 pr-2">
                  <div class="text-subtitle-2 font-weight-bold text-truncate">
                    {{ $t(notification.type) }}
                  </div>
                  <v-chip
                      v-if="notification.data.test"
                      size="x-small"
                      color="warning"
                      variant="flat"
                      label
                      class="ml-2 flex-shrink-0"
                  >
                    TEST
                  </v-chip>
                </div>
                <div class="d-flex align-center flex-shrink-0">
                  <v-tooltip :text="getExactDate(notification)" location="bottom">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-caption text-medium-emphasis mr-2 notification-date">
                        {{ $dayjs(notification.created_at).fromNow() }}
                      </span>
                    </template>
                  </v-tooltip>
                  <v-btn
                      variant="text"
                      icon
                      density="compact"
                      size="small"
                      class="action-btn"
                      :color="notification.read_at ? 'medium-emphasis' : 'primary'"
                      @click.stop="markAs(notification)"
                      :title="notification.read_at ? 'Marquer comme non lu' : 'Marquer comme lu'"
                  >
                    <v-icon :icon="notification.read_at ? 'mdi-circle-outline' : 'mdi-circle'" size="x-small"></v-icon>
                  </v-btn>
                </div>
              </div>

              <template v-if="hasDetails(notification)">
                <div v-if="getHeadline(notification)" class="text-body-2 text-truncate">
                  {{ getHeadline(notification) }}
                </div>
                <div v-if="notification.data.cause" class="text-body-2 text-medium-emphasis text-truncate-2">
                  {{ notification.data.cause }}
                </div>
              </template>
              <div v-else class="text-body-2 text-medium-emphasis text-truncate-2">
                {{ notification.data.message }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasMore || loading" class="d-flex justify-center border-t pa-2">
          <v-btn
              variant="text"
              size="small"
              color="primary"
              class="text-none"
              :loading="loading"
              @click="loadMore"
          >
            Voir plus
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import routeName from '~/utils/route-name'

const CATEGORY_ALERTS = 'alerts'
const CATEGORY_ALL = 'all'
const WARNING_TYPES = ['CAMPAIGN_STUCK', 'CAMPAIGN_REPORTS_MISSING', 'ROUTING_PAUSED']
const PER_PAGE = 15

const {user, getMe} = useAuth()
const notifications: Ref<DbNotification[]> = ref([])
const {$customFetch} = useNuxtApp()

const category = ref(CATEGORY_ALERTS)
const onlyUnread = ref(true)
const hasMore = ref(false)
const loading = ref(false)
const markingAll = ref(false)
let requestId = 0

const unreadCount = computed(() => category.value === CATEGORY_ALERTS
    ? user.value?.unread_alerts_count
    : user.value?.unread_notifications_count)

useUserSync().setupListeners({
    onNotification: () => refresh()
})

onMounted(() => refresh())

watch([category, onlyUnread], () => refresh())

function categoryParam() {
  return category.value === CATEGORY_ALERTS ? CATEGORY_ALERTS : undefined
}

function refresh() {
  notifications.value = []
  getNotifications(1, PER_PAGE)
}

/**
 * The API pages by offset over the current filter: once a row has left the unread list, the next
 * page would start one row too far. Load the rows that follow the displayed ones instead.
 */
function loadMore() {
  if (loading.value || !hasMore.value) {
    return
  }

  const loaded = notifications.value.length

  if (!loaded) {
    refresh()
  } else if (loaded % PER_PAGE === 0) {
    getNotifications(loaded / PER_PAGE + 1, PER_PAGE)
  } else {
    getNotifications(2, loaded)
  }
}

async function getNotifications(pageToLoad: number, perPage: number) {
  const currentRequest = ++requestId
  loading.value = true

  try {
    const {data, meta} = await $customFetch<ApiResponseList<DbNotification>>('/api/users/' + user.value.id + '/notifications', {
      method: 'GET',
      params: {
        page: pageToLoad,
        perPage,
        type: onlyUnread.value ? 'unread' : null,
        category: categoryParam(),
      }
    })

    if (currentRequest !== requestId) {
      return
    }

    const loadedIds = new Set(notifications.value.map(notification => notification.id))
    notifications.value.push(...data.filter(notification => !loadedIds.has(notification.id)))
    hasMore.value = !!meta && meta.current_page < meta.last_page
  } finally {
    if (currentRequest === requestId) {
      loading.value = false
    }
  }
}

function getNotificationIcon(type: string) {
  if (type === 'CAMPAIGN_STUCK') {
    return 'mdi-progress-alert'
  } else if (type.includes('ROUTING')) {
    return 'mdi-rocket-launch-outline'
  } else if (type.includes('QUERY')) {
    return 'mdi-database-search-outline'
  } else {
    return 'mdi-bell-ring-outline'
  }
}

function getNotificationColor(type: string) {
  if (type.includes('FAILED') || type.endsWith('_ERROR')) {
    return 'error'
  } else if (WARNING_TYPES.includes(type)) {
    return 'warning'
  } else {
    return 'info'
  }
}

function getCampaignLink(notification: DbNotification) {
  return notification.data?.id
      ? {name: routeName.campaignShow, params: {id: notification.data.id}}
      : null
}

function hasDetails(notification: DbNotification) {
  return !!(notification.data.campaign_name || notification.data.cause)
}

function getHeadline(notification: DbNotification) {
  return [notification.data.campaign_name, notification.data.step]
      .filter(Boolean)
      .join(' · ')
}

function getExactDate(notification: DbNotification) {
  return useDayjsFormat(notification.created_at)
}

function open(notification: DbNotification) {
  const link = getCampaignLink(notification)

  if (!link) {
    if (!notification.read_at) {
      markAs(notification)
    }
    return
  }

  navigateTo(link)
  setTimeout(() => {
    if (!notification.read_at) {
      markAs(notification)
    }
  }, 1000)
}

async function markAs(notification: DbNotification) {
  const {data: response} = await $customFetch<ApiResponse<DbNotification>>('/api/users/' + user.value.id + '/notifications/' + notification.id, {
    method: 'PUT',
  })

  const index = notifications.value.findIndex((item: { id: string }) => {
    return item.id === notification.id
  })

  const current = notifications.value[index]

  if (current) {
    if (response.read_at && onlyUnread.value) {
      notifications.value.splice(index, 1)

      if (!notifications.value.length && hasMore.value) {
        refresh()
      }
    } else {
      Object.assign(current, response)
    }
  }

  await getMe()
}

async function markAllAsRead() {
  markingAll.value = true

  try {
    await $customFetch('/api/users/' + user.value.id + '/notifications/read-all', {
      method: 'PUT',
      params: {
        category: categoryParam(),
      }
    })

    refresh()
    await getMe()
  } finally {
    markingAll.value = false
  }
}
</script>

<style scoped>
.notification-item:hover {
  background-color: rgb(var(--v-theme-surface-variant), 0.05);
}

.notification-item:hover .action-btn {
  opacity: 1;
}



.min-w-0 {
  min-width: 0;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
